import {FormEvent, useCallback, useMemo, useState} from "react";
import {styled} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@memaro/components/Button.tsx";
import Form from "@memaro/components/Form.tsx";
import Alert from "@mui/material/Alert";
import {useAuth} from "../lib/auth.ts";
import {
    EmptyConfirmResetPasswordConfirmationCode,
    EmptyConfirmResetPasswordNewPassword,
    EmptyConfirmResetPasswordUsername,
    ExpiredCodeException,
    InvalidPasswordException,
    PasswordsDoNotMatch,
    nameFromError
} from "@memaro/auth";
import {useFormInput} from "@memaro/forms";
import {useRouter} from "../hooks/useRouter";
import {useFeedback} from "@memaro/feedback";
import {unauthenticated} from "../hoc/auth.tsx";

const StyledStack = styled(Stack)({
    width: 'fit-content',
    margin: '0 auto'
});

const StyledTextField = styled(TextField)({
    marginTop: 30,
    minWidth: 300,
    fontFamily: "inherit"
})

const ResetPasswordConfirmPage = unauthenticated(() => {
    const {gotoLogin} = useRouter();
    const {confirmResetPassword, signUpUsername} = useAuth();
    const {success} = useFeedback()

    const [email, setEmail] = useFormInput(signUpUsername)
    const [confirmationCode, setConfirmationCode] = useFormInput()
    const [newPassword, setNewPassword] = useFormInput()
    const [repeatNewPassword, setRepeatNewPassword] = useFormInput()
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    const handleResetConfirm = useCallback(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (newPassword != repeatNewPassword) {
            setError(PasswordsDoNotMatch)
            return
        }
        setLoading(true)
        try {
            await confirmResetPassword({
                username: email,
                confirmationCode,
                newPassword,
            });
            success?.("Uw nieuwe wachtwoord is succesvol opgeslagen. U kunt nu inloggen.")
            gotoLogin()
        } catch (e: unknown) {
            setError(nameFromError(e))
        } finally {
            setLoading(false)
        }
    }, [confirmResetPassword, email, confirmationCode, newPassword, repeatNewPassword, success, gotoLogin])

    const errorText = useMemo(() => {
        switch (error) {
            case "":
                return ""
            case EmptyConfirmResetPasswordUsername:
                return "Email adres mag niet leeg zijn"
            case EmptyConfirmResetPasswordNewPassword:
                return "Nieuw wachtwoord mag niet leeg zijn"
            case EmptyConfirmResetPasswordConfirmationCode:
                return "Bevestigingscode mag niet leeg zijn"
            case ExpiredCodeException:
                return "Bevestigingscode is ongeldig/verlopen"
            case PasswordsDoNotMatch:
                return "Opgegeven wachtwoorden komen niet overeen"
            case InvalidPasswordException:
                return "Wachtwoord voldoet niet aan alle eisen"
            default:
                console.error("Unknown error:", error)
                return "Onbekende fout opgetreden"
        }
    }, [error])

    return (
        <StyledStack>
            <Form onSubmit={handleResetConfirm}>
                <h1>Nieuw wachtwoord instellen</h1>
                {errorText ? <Alert severity="error">{errorText}</Alert> : null}
                <StyledTextField type="email" label="Email adres" autoComplete="username" value={email} onChange={setEmail} />
                <StyledTextField label="Bevestigingscode" value={confirmationCode} onChange={setConfirmationCode} autoFocus />
                <StyledTextField type="password" label="Nieuw wachtwoord"  value={newPassword} onChange={setNewPassword} />
                <StyledTextField type="password" label="Herhaal nieuw wachtwoord"  value={repeatNewPassword} onChange={setRepeatNewPassword} />
                <Box sx={{display: "flex", flexDirection: "row-reverse",}}>
                    <Box>
                        <Button type="submit" color="primary" sx={{marginTop: "10px", marginLeft: "10px"}} loading={loading}>Wachtwoord opslaan</Button>
                    </Box>
                </Box>
            </Form>
        </StyledStack>
    )
})

export default ResetPasswordConfirmPage
